import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Button } from "../components/ui/Button";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { Input, Textarea } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { AppSetting } from "../api/types";

interface SettingsEditorProps {
  title: string;
  keyPrefix: string;
  helperText: string;
}

/**
 * Shared by SettingsPage and AdsConfigPage — same underlying app_settings
 * table (migration 0019), filtered by key prefix. Values are stored as
 * JSON, so this editor accepts either a plain string/number or a raw JSON
 * object/array, parsed before sending.
 */
export function SettingsEditor({ title, keyPrefix, helperText }: SettingsEditorProps) {
  const { rows, isLoading, error, reload } = usePagedList<AppSetting>("/admin/settings", { prefix: keyPrefix });
  const [editing, setEditing] = useState<{ key: string; valueText: string; description: string } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AppSetting | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  function openNew() {
    setEditing({ key: keyPrefix, valueText: "", description: "" });
    setIsNew(true);
  }

  function openEdit(setting: AppSetting) {
    setEditing({
      key: setting.key,
      valueText: typeof setting.value === "string" ? setting.value : JSON.stringify(setting.value, null, 2),
      description: setting.description ?? "",
    });
    setIsNew(false);
  }

  async function save() {
    if (!editing?.key) {
      showToast("Key is required", "error");
      return;
    }
    let parsedValue: unknown = editing.valueText;
    try {
      parsedValue = JSON.parse(editing.valueText);
    } catch {
      // Not valid JSON — treat it as a plain string value, which is a
      // perfectly normal setting value.
    }
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/settings/${encodeURIComponent(editing.key)}`, {
        method: "PATCH",
        body: { value: parsedValue, description: editing.description || undefined },
      });
      showToast("Saved", "success");
      reload();
      setEditing(null);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save setting", "error");
    } finally {
      setIsSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/settings/${encodeURIComponent(pendingDelete.key)}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete", "error");
    } finally {
      setIsSubmitting(false);
      setPendingDelete(null);
    }
  }

  const columns: Column<AppSetting>[] = [
    { header: "Key", render: (row) => <span className="font-mono text-xs">{row.key}</span> },
    {
      header: "Value",
      render: (row) => <span className="max-w-xs truncate text-xs text-ink-muted">{typeof row.value === "string" ? row.value : JSON.stringify(row.value)}</span>,
    },
    { header: "Description", render: (row) => <span className="text-xs text-ink-faint">{row.description ?? "—"}</span> },
    { header: "Updated", render: (row) => new Date(row.updated_at).toLocaleDateString() },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title={title}>
      <div className="mb-4 flex justify-between">
        <p className="max-w-lg text-xs text-ink-faint">{helperText}</p>
        <Button onClick={openNew}>+ Add Setting</Button>
      </div>

      <DataTable columns={columns} rows={rows} keyField={(r) => r.key} isLoading={isLoading} error={error} onRetry={reload} emptyTitle="No settings configured yet" />

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title={isNew ? "Add Setting" : `Edit ${editing?.key}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button onClick={save} isLoading={isSubmitting}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input
            label="Key"
            value={editing?.key ?? ""}
            disabled={!isNew}
            onChange={(e) => setEditing((p) => (p ? { ...p, key: e.target.value } : p))}
            hint={isNew ? `Must start with "${keyPrefix}"` : undefined}
          />
          <Textarea
            label="Value (plain text, a number, or JSON)"
            rows={4}
            value={editing?.valueText ?? ""}
            onChange={(e) => setEditing((p) => (p ? { ...p, valueText: e.target.value } : p))}
          />
          <Input label="Description (optional)" value={editing?.description ?? ""} onChange={(e) => setEditing((p) => (p ? { ...p, description: e.target.value } : p))} />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isSubmitting}
        isDangerous
        title={`Delete "${pendingDelete?.key}"?`}
        description="This removes the setting entirely — any code reading it will fall back to its own default, if any."
        confirmLabel="Delete"
      />
    </AppShell>
  );
}
